import { createContext, useEffect, useReducer } from 'react';
import { applicationSteps } from '../../gogurt/routes/private';
import { getStepFromPath, RouteItem } from '../../routes/RouteItem';
import {
  ApplicationProgressContext,
  ApplicationProgressState,
  ApplicationStepAction,
} from './Progress.types';

const ProgressStore = createContext<ApplicationProgressContext>(undefined!);

export const getSavedProgress = (): ApplicationProgressState | null => {
  try {
    const savedProgress: ApplicationProgressState = JSON.parse(
      localStorage.getItem('selfreg.progress')!,
    );
    if (
      savedProgress?.steps != null &&
      savedProgress?.currentStepPath != null
    ) {
      return {
        steps: savedProgress.steps,
        currentStepPath: savedProgress.currentStepPath,
      };
    }
  } catch (err) {
    // fall through to return null
  }
  return null;
};

const getInitialState = (): ApplicationProgressState => {
  const savedProgress = getSavedProgress();
  if (savedProgress != null) {
    return savedProgress;
  }

  let steps: Partial<ApplicationProgressState['steps']> = {};

  for (const stepKey in applicationSteps) {
    const step = applicationSteps[stepKey as keyof typeof applicationSteps];
    steps[step.id!] = {
      id: step.id,
      title: step.title,
      validationState: 'incomplete',
    };
  }

  return {
    steps: steps as ApplicationProgressState['steps'],
    currentStepPath: '/app/welcome',
  };
};

const storeReducer = (
  state = getInitialState(),
  action: ApplicationStepAction,
): ApplicationProgressState => {
  const { steps } = state;

  switch (action.type) {
    case 'SET_CURRENT_STEP':
      const newStepPath =
        (
          getStepFromPath(
            action.payload.pathname,
            applicationSteps,
          ) as RouteItem
        )?.path || (null as any);
      return {
        ...state,
        lastVisitedRoute: state.currentStepPath,
        currentStepPath: newStepPath,
      };
    case 'SET_STEP_VALIDATION_STATE':
      const { id, validationState } = action.payload;
      return {
        ...state,
        steps: {
          ...steps,
          [id]: {
            ...steps[id],
            validationState,
          },
        },
      };
    default:
      return state;
  }
};

const ProgressProvider = ({ children }) => {
  const [state, dispatch] = useReducer(storeReducer, getInitialState());

  useEffect(() => {
    if (
      state?.currentStepPath != null &&
      state?.currentStepPath !== '/welcome'
    ) {
      localStorage.setItem('selfreg.progress', JSON.stringify(state));
    }
  }, [state]);

  const localContext: ApplicationProgressContext = {
    state: state,
    dispatch: dispatch,
  };

  return (
    <ProgressStore.Provider value={localContext}>
      {children}
    </ProgressStore.Provider>
  );
};

export { ProgressStore, ProgressProvider };
